import type { Card, SpecState, SpecSync } from '../types';

/**
 * A short fingerprint of a text, insensitive to line endings and to whitespace at the ends of lines and of
 * the text, so reformatting does not count as a change. FNV-1a, which is plenty for change detection.
 */
export function fingerprint(text: string): string {
  const normalized = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trimEnd())
    .join('\n')
    .trim();
  let hash = 0x811c9dc5;
  for (let i = 0; i < normalized.length; i++) {
    hash ^= normalized.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${(hash >>> 0).toString(16).padStart(8, '0')}:${normalized.length}`;
}

export function syncOf(code: string, spec: string): SpecSync {
  return { code: fingerprint(code), spec: fingerprint(spec) };
}

/** Where the spec stands relative to the code, from the card's last agreed fingerprints. */
export function specState(card: Pick<Card, 'code' | 'spec' | 'specSync'>): SpecState {
  if (!card.spec.trim()) return 'empty';
  if (!card.specSync) return 'unchecked';
  const specSame = fingerprint(card.spec) === card.specSync.spec;
  const codeSame = fingerprint(card.code) === card.specSync.code;
  if (specSame && codeSame) return 'in-step';
  // An edited spec is a change of intent, so it wins when both moved: the code should follow it.
  if (!specSame) return 'spec-changed';
  return 'code-changed';
}

export const SPEC_STATE_LABEL: Record<SpecState, string> = {
  empty: 'No spec yet',
  'in-step': 'In step with the code',
  'code-changed': 'Code changed since the spec',
  'spec-changed': 'Spec edited, code not updated',
  unchecked: 'Not checked against the code',
};
